import React, { useState } from 'react';
import { Property } from '../../../../types';
import { PropertyHealthAudit } from '../../../../types/inventoryHealth';
import { HealthEditFormFields } from './HealthEditFormFields';
import { X, Save, AlertCircle } from 'lucide-react';

interface HealthQuickEditModalProps {
  audit: PropertyHealthAudit | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: string, updates: Partial<Property>) => Promise<boolean>;
}

export const HealthQuickEditModal: React.FC<HealthQuickEditModalProps> = ({ audit, isOpen, onClose, onSave }) => {
  if (!isOpen || !audit) return null;
  const p = audit.property;

  const [piso, setPiso] = useState(p.piso ?? '');
  const [ascensor, setAscensor] = useState<string>(p.ascensor !== undefined && p.ascensor !== null ? String(p.ascensor) : '');
  const [adminFee, setAdminFee] = useState<number | string>(p.valor_administracion || '');
  const [estrato, setEstrato] = useState<number | string>(p.estrato || '');
  const [antiguedad, setAntiguedad] = useState<number | string>(p.antiguedad !== undefined && p.antiguedad !== null ? p.antiguedad : '');
  const [lat, setLat] = useState<number | string>(p.latitude ?? '');
  const [lng, setLng] = useState<number | string>(p.longitude ?? '');
  const [area, setArea] = useState<number | string>(p.area_construida || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    const updates: Partial<Property> = {
      piso: piso !== '' ? (isNaN(Number(piso)) ? String(piso) : Number(piso)) : null,
      ascensor: ascensor !== '' ? Number(ascensor) : null,
      num_ascensores: ascensor !== '' ? Number(ascensor) : null,
      valor_administracion: adminFee !== '' ? Number(String(adminFee).replace(/[^0-9]/g, '')) : 0,
      estrato: estrato !== '' ? Number(estrato) : (null as any),
      antiguedad: antiguedad !== '' ? Number(antiguedad) : null,
      latitude: lat !== '' ? Number(lat) : null,
      longitude: lng !== '' ? Number(lng) : null,
      area_construida: area !== '' ? Number(area) : undefined,
    };
    const ok = await onSave(p.id, updates);
    setIsSaving(false);
    if (ok) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div>
            <span className="text-xs font-black uppercase tracking-wider text-sky-600">Corrección Rápida</span>
            <h3 className="text-base font-black text-gray-800">Inmueble {p.codigo || p.id}</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto custom-scrollbar">
          {audit.issues.length > 0 && (
            <div className="p-3 bg-amber-50/70 border border-amber-200 rounded-2xl space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs font-bold text-amber-800">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <span>Observaciones a corregir ({audit.issues.length}):</span>
              </div>
              <ul className="text-[11px] text-amber-700 pl-5 list-disc space-y-0.5">
                {audit.issues.map((iss, idx) => (
                  <li key={idx}><strong>{iss.ruleName}:</strong> {iss.message}</li>
                ))}
              </ul>
            </div>
          )}

          <HealthEditFormFields
            adminFee={adminFee}
            setAdminFee={setAdminFee}
            piso={piso}
            setPiso={setPiso}
            ascensor={ascensor}
            setAscensor={setAscensor}
            estrato={estrato}
            setEstrato={setEstrato}
            antiguedad={antiguedad}
            setAntiguedad={setAntiguedad}
            area={area}
            setArea={setArea}
            lat={lat}
            setLat={setLat}
            lng={lng}
            setLng={setLng}
          />

          <div className="pt-3 border-t border-gray-100 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center gap-1.5 px-5 py-2 text-xs font-black uppercase tracking-wider bg-sky-600 hover:bg-sky-700 text-white rounded-xl shadow-md transition-all disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" />
              <span>{isSaving ? 'Guardando...' : 'Guardar Corrección'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
